import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Loader2,
  MailPlus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  OrganizationInvite,
  OrganizationUser,
  OrganizationUserRole,
  useOrganizationUsers,
} from '@/hooks/useOrganizationUsers';

interface OrganizationMembersTabProps {
  isAdmin: boolean;
  currentUserId?: string;
}

type ConfirmAction =
  | { type: 'removeUser'; user: OrganizationUser }
  | { type: 'revokeInvite'; invite: OrganizationInvite }
  | null;

const roleLabels: Record<OrganizationUserRole, string> = {
  admin: 'Admin',
  analyst: 'Analyst',
  viewer: 'Viewer',
};

function roleBadgeVariant(role: OrganizationUserRole): 'default' | 'secondary' | 'outline' {
  if (role === 'admin') return 'default';
  if (role === 'analyst') return 'secondary';
  return 'outline';
}

function formatDate(value: unknown): string {
  if (!value) return 'N/A';

  let date: Date | null = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string' || typeof value === 'number') {
    date = new Date(value);
  } else if (typeof value === 'object' && value !== null) {
    const maybeTimestamp = value as { toDate?: () => Date; seconds?: number };
    if (typeof maybeTimestamp.toDate === 'function') {
      date = maybeTimestamp.toDate();
    } else if (typeof maybeTimestamp.seconds === 'number') {
      date = new Date(maybeTimestamp.seconds * 1000);
    }
  }

  if (!date || Number.isNaN(date.getTime())) return 'N/A';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export const OrganizationMembersTab: React.FC<OrganizationMembersTabProps> = ({
  isAdmin,
  currentUserId,
}) => {
  const {
    users,
    pendingInvites,
    loading,
    mutating,
    error,
    loadOrganizationUsers,
    inviteUser,
    promoteUserToAdmin,
    removeUser,
    revokeInvite,
    resendInvite,
  } = useOrganizationUsers(isAdmin);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteDisplayName, setInviteDisplayName] = useState('');
  const [inviteRole, setInviteRole] = useState<OrganizationUserRole>('analyst');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  useEffect(() => {
    if (isAdmin) {
      loadOrganizationUsers().catch((err) => {
        toast.error(err.message || 'Failed to load organization users');
      });
    }
  }, [isAdmin, loadOrganizationUsers]);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      if (a.role === b.role) return a.email.localeCompare(b.email);
      if (a.role === 'admin') return -1;
      if (b.role === 'admin') return 1;
      return a.role.localeCompare(b.role);
    });
  }, [users]);

  if (!isAdmin) {
    return null;
  }

  async function handleInvite(event: React.FormEvent) {
    event.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      toast.error('Enter an email address');
      return;
    }

    try {
      const result = await inviteUser({
        email,
        displayName: inviteDisplayName.trim() || undefined,
        role: inviteRole,
      });
      toast.success(result.message || 'Invitation sent');
      setInviteEmail('');
      setInviteDisplayName('');
      setInviteRole('analyst');
    } catch (err: any) {
      toast.error(err.message || 'Failed to invite user');
    }
  }

  async function handlePromote(user: OrganizationUser) {
    if (user.role === 'admin') return;
    try {
      const result = await promoteUserToAdmin(user.uid);
      toast.success(result.message || `${user.email} is now an admin`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to promote user');
    }
  }

  async function handleConfirmAction() {
    if (!confirmAction) return;

    try {
      if (confirmAction.type === 'removeUser') {
        const result = await removeUser(confirmAction.user.uid);
        toast.success(result.message || 'User removed');
      } else {
        const result = await revokeInvite(confirmAction.invite.id);
        toast.success(result.message || 'Invitation revoked');
      }
      setConfirmAction(null);
    } catch (err: any) {
      toast.error(err.message || 'Action failed');
    }
  }

  async function handleResendInvite(invite: OrganizationInvite) {
    try {
      const result = await resendInvite(invite.id);
      toast.success(result.message || 'Invitation resent');
    } catch (err: any) {
      toast.error(err.message || 'Failed to resend invitation');
    }
  }

  return (
    <div className="space-y-6">
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Organization Members
              </CardTitle>
              <CardDescription>
                Invite teammates, promote non-admins, and remove non-admin users.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadOrganizationUsers()}
              disabled={loading || mutating}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleInvite} className="rounded-lg border border-border bg-muted/20 p-4">
            <div className="mb-4 flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium text-foreground">Invite User</h3>
            </div>
            <div className="grid gap-4 lg:grid-cols-[1fr_1fr_180px_auto] lg:items-end">
              <div className="space-y-2">
                <Label htmlFor="organizationInviteEmail">Email</Label>
                <Input
                  id="organizationInviteEmail"
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder="teammate@firm.com"
                  disabled={mutating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="organizationInviteDisplayName">Name</Label>
                <Input
                  id="organizationInviteDisplayName"
                  value={inviteDisplayName}
                  onChange={(event) => setInviteDisplayName(event.target.value)}
                  placeholder="Optional"
                  disabled={mutating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="organizationInviteRole">Role</Label>
                <Select
                  value={inviteRole}
                  onValueChange={(value) => setInviteRole(value as OrganizationUserRole)}
                  disabled={mutating}
                >
                  <SelectTrigger id="organizationInviteRole">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="analyst">Analyst</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={mutating || !inviteEmail.trim()}>
                {mutating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <MailPlus className="mr-2 h-4 w-4" />
                )}
                Invite
              </Button>
            </div>
          </form>

          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                      No active organization users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedUsers.map((member) => {
                    const isSelf = member.uid === currentUserId;
                    const isAdminMember = member.role === 'admin';
                    const canRemove = !isSelf && !isAdminMember && member.status === 'active';
                    const canPromote = !isAdminMember;

                    return (
                      <TableRow key={member.uid || member.email}>
                        <TableCell>
                          <div>
                            <div className="font-medium text-foreground">
                              {member.displayName || member.email}
                              {isSelf && <span className="ml-2 text-xs text-muted-foreground">(You)</span>}
                            </div>
                            <div className="text-sm text-muted-foreground">{member.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={roleBadgeVariant(member.role)}>
                            {roleLabels[member.role] || member.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={member.status === 'active' ? 'success' : 'outline'}>
                            {member.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(member.createdAt)}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            {canPromote && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePromote(member)}
                                disabled={mutating}
                              >
                                <ShieldCheck className="mr-2 h-4 w-4" />
                                Promote
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setConfirmAction({ type: 'removeUser', user: member })}
                              disabled={mutating || !canRemove}
                              title={
                                isSelf
                                  ? 'You cannot remove yourself'
                                  : isAdminMember
                                    ? 'Admins cannot be removed here'
                                    : member.status !== 'active'
                                      ? 'Only active users can be removed'
                                    : undefined
                              }
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle>Pending Invitations</CardTitle>
          <CardDescription>
            Revoke or resend open invitations that have not yet been accepted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invitee</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingInvites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    No pending invitations.
                  </TableCell>
                </TableRow>
              ) : (
                pendingInvites.map((invite) => (
                  <TableRow key={invite.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium text-foreground">
                          {invite.displayName || invite.email}
                        </div>
                        <div className="text-sm text-muted-foreground">{invite.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={roleBadgeVariant(invite.role)}>
                        {roleLabels[invite.role] || invite.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(invite.emailSentAt || invite.createdAt)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(invite.expiresAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResendInvite(invite)}
                          disabled={mutating}
                        >
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Resend
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmAction({ type: 'revokeInvite', invite })}
                          disabled={mutating}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Revoke
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'removeUser' ? 'Remove organization user?' : 'Revoke invitation?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === 'removeUser'
                ? `This will remove ${confirmAction.user.email} from the organization. Admins and your own account cannot be removed from this UI.`
                : `This will revoke the pending invitation for ${confirmAction?.invite.email}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              disabled={mutating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {mutating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
